import './shim/process-env';

// Import Bagisto's shop.js entry point (sets window.Vue)
import '../../../packages/Webkul/Shop/src/Resources/assets/js/app';
import { flushVueDeferredRegistry } from './shim/vue-deferred-registry';

flushVueDeferredRegistry(window.Vue);

import { patchVueObservable } from './shim/vue-observable';
import App from './class/App';
import { pascalToKebab } from './utils/String';
import VModal from 'vue-js-modal';
import smoothscroll from 'smoothscroll-polyfill';
import CustomerProjectCard from './components/CustomerProjectCard';
import CupsSelection from './components/CupsSelection';
import CollapsibleContent from './components/CollapsibleContent';
import Configurator from './components/Configurator/Configurator';
import QuestionAnswerAccordion from './components/QuestionAnswerAccordion';
import ListingFilters from './components/Listing/ListingFilters';
import ListingSelect from './components/Listing/ListingSelect';
import CheckingFilesModal from './components/Checkout/CheckingFilesModal';
import InstagramWall from './components/InstagramWall.vue';
import GetQuotation from './components/Checkout/GetQuotation';
import ProductPageSlider from './components/Product/ProductPageSlider';
import VueMatchMedia from '@webqam/vue-match-media';
import breakpoints from './utils/breakpoints';
import PortalVue from 'portal-vue';
import store from './store';
import VueI18n from 'vue-i18n';
import i18nConfig from './i18nConfig';
import { initTarteAuCitron } from './tarteaucitron';
import { initSwiper } from './swiper';
import { initZendesk } from './zendesk';
import { initInformationBanner } from './information-banner';
import fr from 'vee-validate/dist/locale/fr';
import { Validator } from 'vee-validate';
import LgfSpinner from './components/LgfSpinner';
import VueKonva from 'vue-konva';
import Konva from 'konva';
import { initObf } from './obf';
import { initAvisGarantis } from './avis-garantis';
import { initKlaviyo } from './klaviyo';
import IconLogoMastercard from './components/Icons/IconLogoMastercard.vue';
import IconLogoPaypal from './components/Icons/IconLogoPaypal.vue';
import IconLogoPaypalMonogram from './components/Icons/IconLogoPaypalMonogram.vue';
import IconLogoVisa from './components/Icons/IconLogoVisa.vue';
import IconLogoBankCheque from './components/Icons/IconLogoBankCheque.vue';
import IconLogoBankTransfer from './components/Icons/IconLogoBankTransfer.vue';
import IconLogoSepa from './components/Icons/IconLogoSepa.vue';
import IconLogoGoogle from './components/Icons/IconLogoGoogle.vue';
import IconLogoApple from './components/Icons/IconLogoApple.vue';
import IconLogoKlarna from './components/Icons/IconLogoKlarna.vue';
import Tracking from './utils/Tracking';
import PasswordInput from './components/PasswordInput.vue';
import UniqueEmailInput from './components/UniqueEmailInput.vue';
import AddressInput from './components/AddressInput.vue';
import AddressFormToggle from './components/AddressFormToggle.vue';
import CheckoutInput from './components/Checkout/CheckoutInput.vue';
import RelayPicker from './components/Checkout/RelayPicker.vue';
import RelayOpeningHours from './components/Checkout/RelayOpeningHours.vue';
import Stripe from './components/Checkout/Stripe.vue';
import StripeKlarna from './components/Checkout/StripeKlarna.vue';
import StripeRequestButton from './components/Checkout/StripeRequestButton.vue';
import Paypal from './components/Checkout/Paypal.vue';
import PaymentButton from './components/Checkout/PaymentButton.vue';
import CustomerDeleteButton from './components/CustomerDeleteButton.vue';
import OrderItemsConfirmation from './components/Checkout/OrderItemsConfirmation.vue';
import TextCopyButton from './components/TextCopyButton.vue';
import ToastMessage from './components/ToastMessage.vue';
import CreateCustomerProjectButton from './components/Checkout/CreateCustomerProjectButton.vue';
import QuestionCategorySelect from './components/QuestionCategorySelect';
import BirthdateRepeatableField from './components/BirthdateRepeatableField.vue';
import FidelityProgramSlider from './components/FidelityProgramSlider';

patchVueObservable(window.Vue);

const Vue = window.Vue;

Tracking.init();

Validator.localize('fr', fr);

smoothscroll.polyfill();

Vue.use(VModal);
Vue.use(VueMatchMedia, { breakpoints });
Vue.use(PortalVue);
Vue.use(VueI18n);
Vue.use(VueKonva);

// Konva renders every layer canvas at devicePixelRatio (3 on iOS). For the
// configurator texture canvas (sampled as a texture, already rasterized at an
// explicit DPI) this is pure waste: ~9x the canvas memory, which crashes the
// Safari iOS tab when rendering several cross-sell products and overflows
// Safari's 16M px canvas limit at print resolution. Pin it to 1 globally.
Konva.pixelRatio = 1;

window.modalStateObservable = Vue.observable({
  isCheckingFilesModalActive: false,
  isRelayModalActive: false,
  is3DSecureModalActive: false,
  isGetQuotationModalActive: false,
  isAuthModalActive: false,
  isCustomerProjectModalActive: false,
});

window.threeDSecureStateObservable = Vue.observable({
  url: '',
});

if (window.__loadTarteAuCitron) {
  window.__loadTarteAuCitron();
}

[
  CustomerProjectCard,
  CupsSelection,
  CollapsibleContent,
  Configurator,
  ListingFilters,
  ListingSelect,
  QuestionAnswerAccordion,
  QuestionCategorySelect,
  CheckingFilesModal,
  InstagramWall,
  LgfSpinner,
  GetQuotation,
  ProductPageSlider,
  IconLogoMastercard,
  IconLogoPaypal,
  IconLogoPaypalMonogram,
  IconLogoVisa,
  IconLogoBankCheque,
  IconLogoBankTransfer,
  IconLogoSepa,
  IconLogoGoogle,
  IconLogoApple,
  IconLogoKlarna,
  PasswordInput,
  UniqueEmailInput,
  AddressInput,
  AddressFormToggle,
  CheckoutInput,
  RelayPicker,
  RelayOpeningHours,
  Stripe,
  StripeKlarna,
  StripeRequestButton,
  Paypal,
  PaymentButton,
  CustomerDeleteButton,
  OrderItemsConfirmation,
  TextCopyButton,
  ToastMessage,
  CreateCustomerProjectButton,
  BirthdateRepeatableField,
  FidelityProgramSlider,
].forEach(c => {
  if (process.env.NODE_ENV === 'development' && !c.name) {
    throw new Error('Component name is undefined.');
  }

  Vue.component(pascalToKebab(c.name), c);
});

const i18n = new VueI18n(i18nConfig);

const appReady = new Promise(resolve => {
  // Hack incoming… (sorry)
  // Registering a global mixin to be able to modify the root instance of the
  // app which is already declared in Bagisto's entry point
  Vue.mixin({
    // Register Vue-i18n's config and Vuex store
    i18n,
    store,
    // Mounted hook (called when the app is rendered in DOM in addition to
    // internal mounted() method if any)
    // Use with caution
    mounted() {
      if (this.$root === this) {
        resolve();
      }
    },
  });
});

appReady.then(() => {
  new App();

  document.dispatchEvent(new CustomEvent('vue-app-mounted'));
});

initTarteAuCitron();
initSwiper();
initZendesk();
initObf();
initAvisGarantis();
initKlaviyo();
initInformationBanner();
